// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState } from "react";
import { Trash2, Archive, Download, Search } from "lucide-react";
import gdprService from "../../../services/gdprService";

const DataManagementTab = ({
  dataRecords,
  currentPage,
  pageSize,
  totalRecords,
  totalPages,
  onPageChange,
  onPageSizeChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color:
          "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200",
        text: "Aktiv",
      },
      canceled: {
        color: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200",
        text: "Storniert",
      },
      soft_deleted: {
        color:
          "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200",
        text: "Soft Delete",
      },
      scheduled_deletion: {
        color: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200",
        text: "Löschung geplant",
      },
      archived: {
        color: "bg-primary-color-tint primary-color",
        text: "Archiviert",
      },
    };

    const config = statusConfig[status];

    if (!config) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-300">
          {status || "Unbekannt"}
        </span>
      );
    }

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  const handleSoftDelete = async (recordId) => {
    try {
      setLoading(true);
      await gdprService.deleteUserData(recordId);
      // Refresh data after deletion
      onPageChange(currentPage, pageSize);
    } catch (error) {
      console.error("Error performing soft delete:", error);
      setError("Fehler beim Soft Delete. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const handleHardDelete = async (recordId) => {
    try {
      setLoading(true);
      await gdprService.deleteUserData(recordId);
      // Refresh data after deletion
      onPageChange(currentPage, pageSize);
    } catch (error) {
      console.error("Error performing hard delete:", error);
      setError("Fehler beim Hard Delete. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const handleDataExport = async (recordId) => {
    try {
      setLoading(true);
      const exportData = await gdprService.exportUserData(recordId);

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `datenexport_${recordId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting data:", error);
      setError("Fehler beim Datenexport. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold dark:text-slate-100">
          Datenverwaltung
        </h2>
        <div className="flex space-x-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Datensätze durchsuchen..."
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-400 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="px-4 py-2 bg-primary-color text-white rounded-lg hover:opacity-90 flex items-center">
            <Download className="w-4 h-4 mr-2" />
            Bulk Export
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-600">
          <thead className="bg-gray-50 dark:bg-slate-700/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 dark:border-slate-500 dark:bg-slate-700 accent-[var(--primary-color)] focus:ring-2 focus:ring-[var(--primary-color)]"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRecords(new Set(dataRecords.map((r) => r.id)));
                    } else {
                      setSelectedRecords(new Set());
                    }
                  }}
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Datensubjekt
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Typ
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Aufbewahrung bis
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Größe
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-600">
            {dataRecords
              .filter(
                (record) =>
                  searchTerm === "" ||
                  record.subject
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  record.type.toLowerCase().includes(searchTerm.toLowerCase()),
              )
              .map((record) => (
                <tr
                  key={record.id}
                  className="hover:bg-gray-50 dark:hover:bg-slate-700/50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 dark:border-slate-500 dark:bg-slate-700 accent-[var(--primary-color)] focus:ring-2 focus:ring-[var(--primary-color)]"
                      checked={selectedRecords.has(record.id)}
                      onChange={(e) => {
                        const newSelection = new Set(selectedRecords);
                        if (e.target.checked) {
                          newSelection.add(record.id);
                        } else {
                          newSelection.delete(record.id);
                        }
                        setSelectedRecords(newSelection);
                      }}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-slate-100">
                      {record.subject}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-slate-400">
                      Erstellt: {record.createdAt}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-300">
                      {record.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(record.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-slate-200">
                    {record.retentionDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                    {record.dataSize}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleDataExport(record.id)}
                      className="primary-color hover:opacity-80 p-1"
                      title="Datenexport"
                      disabled={loading}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleSoftDelete(record.id)}
                      className="primary-color hover:opacity-80 p-1"
                      title="Soft Delete"
                      disabled={record.status !== "active" || loading}
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleHardDelete(record.id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1"
                      title="Hard Delete"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="bg-white dark:bg-slate-800 px-6 py-3 flex items-center justify-between border-t border-gray-200 dark:border-slate-700">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700 dark:text-slate-300">
            Zeige {(currentPage - 1) * pageSize + 1} bis{" "}
            {Math.min(currentPage * pageSize, totalRecords)} von {totalRecords}{" "}
            Einträgen
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <select
            value={pageSize}
            onChange={(e) => {
              const newSize = parseInt(e.target.value);
              onPageSizeChange(newSize);
            }}
            className="px-3 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <button
            onClick={() => onPageChange(currentPage - 1, pageSize)}
            disabled={currentPage <= 1}
            className="px-3 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-slate-600"
          >
            Zurück
          </button>

          <span className="px-3 py-1 text-sm text-gray-700 dark:text-slate-300">
            Seite {currentPage} von {totalPages}
          </span>

          <button
            onClick={() => onPageChange(currentPage + 1, pageSize)}
            disabled={currentPage >= totalPages}
            className="px-3 py-1 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-slate-600"
          >
            Weiter
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataManagementTab;
